import type { ElementRef } from '@angular/core';
import {
  Component,
  computed,
  forwardRef,
  input,
  model,
  viewChild,
} from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import type { OnyxLabelLink } from '../../labels/interfaces';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';

@Component({
  selector: 'onyx-textarea',
  imports: [
    OnyxErrorComponent,
    OnyxInputLabelComponent,
    OnyxErrorComponent,
    TranslatePipe,
  ],
  templateUrl: './onyx-textarea.component.html',
  styleUrls: ['./onyx-textarea.component.scss'],
  host: { '[style.width]': `width()` },
  providers: [
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxTextareaComponent),
    },
  ],
})
export class OnyxTextareaComponent
  extends OnyxBaseFormControlComponent
  implements FormValueControl<string | null>
{
  public readonly value = model<string | null>(null);
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly resize = input(true);
  public readonly minResizeHeight = input<`${string}px`>('120px');
  public readonly maxResizeHeight = input<`${string}px`>('500px');
  public readonly width = input('100%');
  public readonly placeholder = input<string | null>();
  public readonly link = input<OnyxLabelLink | null>(null);
  public readonly maxLength = input<number>();
  public readonly showErrors = input(true);

  protected maxLengthHint = computed(() => {
    if (this.maxLength() == null) return undefined;
    return `${this.value()?.trim()?.length ?? 0}/${this.maxLength()}`;
  });

  private textareaElementRef =
    viewChild.required<ElementRef<HTMLTextAreaElement>>('textareaElement');

  public focus(): void {
    if (this.disabled()) return;
    this.textareaElementRef().nativeElement.focus();
  }

  protected handleValueChange(event: Event): void {
    let value = (event.target as HTMLTextAreaElement).value;
    if (this.maxLength() != null) value = value.slice(0, this.maxLength());

    this.value.set(value || null);
    this.textareaElementRef().nativeElement.value = value;
  }
}
